import { toastr } from "react-redux-toastr";
import { useAuth0 } from "@auth0/auth0-react";
import { Avatar, TextField, Button } from "@material-ui/core";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, IconButton } from "@material-ui/core";
// component
import { formatDate } from "helper/dateFormat";
import AlertDialog from "components/Dialog/index";
import PastoralService from "Lib/API/PastoralApi/index";
import { ReactComponent as Edit } from "assets/icons/EditClass.svg";
import { ReactComponent as Delete } from "assets/icons/Delete.svg";
// direct import
import EditEventDateModal from "./EditEventDateModal";
import MemberEventDrawerUpdates from "./MemberEventDrawerUpdates";
import EditEventDescriptionModal from "./EditEventDescriptionModal";

const MemberEventDrawerContent = ({
  eventType,
  eventDate,
  setEventDate,
  status,
  description,
  contactId,
  eventId,
  openNewTaskDrawer,
  setCallFeedsAgain,
  refetchEventCallback,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [updates, setUpdates] = useState([]);
  const [editData, setEditData] = useState(null);
  const [updateValue, setUpdateValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [updateEvent, setUpdateEvent] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState({});
  const [newUpdateField, setNewUpdateField] = useState(false);
  const [updateModalLoading, setUpdateLoading] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState(false);
  const [showEditDateModal, setShowEditDateModal] = useState(false);
  const [getFollowupLoading, setGetFollowupLoading] = useState(true);
  const [createFollowupLoading, setCreateFollowupLoading] = useState(false);

  const eventInfo = useMemo(() => {
    return [
      {
        id: "eventType",
        title: "Event Type",
        value: eventType,
      },
      {
        id: "eventDate",
        title: "Event Date",
        value: formatDate({ date: eventDate, formatType: "defaultWithUTC" }),
      },
      {
        id: "eventStatus",
        title: "Event Status",
        value: status === 0 ? "Active" : "Archived",
      },
    ];
  }, [eventType, eventDate, status]);

  const handleAddNewUpdate = useCallback(() => {
    setNewUpdateField(true);
  }, []);

  const handleCloseNewUpdate = useCallback(() => {
    setNewUpdateField(false);
    setEditData(null);
  }, []);

  const getFollowupHandler = useCallback(async () => {
    setGetFollowupLoading(true);
    const token = await getAccessTokenSilently();
    PastoralService.getEventsFollowup({ token, eventId })
      .then((res) => {
        if (res) {
          setUpdates(res?.data);
        }
      })
      .catch((error) => {
        toastr.error("Error", error?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setGetFollowupLoading(false);
      });
  }, [eventId, getAccessTokenSilently]);

  useEffect(() => {
    getFollowupHandler();
  }, []);

  const handleSubmitNewUpdate = useCallback(async () => {
    if (createFollowupLoading) return;
    if (!updateValue?.trim()) return setErrorMessage("Please fill the update");

    setCreateFollowupLoading(true);
    const token = await getAccessTokenSilently();
    const bodyData = {
      involvedContactId: contactId?.toString(),
      description: updateValue,
    };
    PastoralService.createEventsFollowup({
      data: bodyData,
      eventId,
      token,
    })
      .then((res) => {
        if (res) {
          getFollowupHandler();
          setCallFeedsAgain((prevState) => !prevState);
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setNewUpdateField(false);
        setCreateFollowupLoading(false);
      });
  }, [contactId, eventId, getAccessTokenSilently, updateValue]);

  const handleEditMessage = (item) => setEditData(item);

  const handleUpdate = async (editData) => {
    if (createFollowupLoading) return;
    if (editData?.memo?.length === 0)
      return setErrorMessage("Please fill the update");

    setCreateFollowupLoading(true);
    const token = await getAccessTokenSilently();
    const bodyData = {
      memo: editData?.memo,
    };
    PastoralService.updateFollowup({
      data: bodyData,
      eventId: editData?.id,
      token,
    })
      .then((res) => {
        if (res) {
          getFollowupHandler();
          setCallFeedsAgain();
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setEditData(null);
        setNewUpdateField(false);
        setCreateFollowupLoading(false);
      });
  };

  const handleRemoveUpdate = async (eventID) => {
    try {
      setUpdateLoading(true);
      const token = await getAccessTokenSilently();
      await PastoralService.deleteUpdateById({
        token,
        id: eventID?.id,
      });
      toastr.success("Success", `Update Delete Successfully!`);
    } catch (error) {
      toastr.error("Error", error?.data?.message || "Something went wrong!");
    } finally {
      setUpdateEvent(false);
      setSelectedUpdate({});
      setUpdateLoading(false);
      getFollowupHandler();
      refetchEventCallback();
    }
  };

  return (
    <div className="pastoral-profile__content mt-2 pt-5 px-5 d-flex flex-column flex-grow-1">
      <div className="sub-h d-flex justify-content-between py-5 border border-right-0 border-left-0 border-light mb-2">
        {eventInfo?.map((item) => (
          <div
            className="border-right border-light w-25 pr-3"
            key={item.id}
            style={{ position: "relative" }}
          >
            <p className="m-0" style={{ color: "#6e6e6e" }}>
              {item.title}
              {item.id === "eventDate" && !showEditDateModal && (
                <button
                  className="ml-2 btn btn-sm btn-link p-0"
                  style={{ position: "absolute", top: -2, right: 16 }}
                  onClick={() => setShowEditDateModal(true)}
                >
                  <Edit />
                </button>
              )}
            </p>
            <p className="font-16">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="mb-5 border-bottom border-light">
        <Box display="flex" justifyContent="space-between">
          <p className="fs-12">Description</p>
          {}
          <button
            className="ml-2 btn btn-sm btn-link p-0"
            onClick={() => {
              setDescriptionModal(true);
            }}
          >
            <Edit />
          </button>
        </Box>
        <p
          className="fs-16 Scroll"
          style={{
            minHeight: 30,
            maxHeight: 120,
            height: "auto",
            wordBreak: "break-word",
            overflowX: "hidden",
          }}
        >
          {description?.length !== 0 ? description : "-"}
        </p>
      </div>

      <p className="fs-12 mb-5">Updates</p>
      <>
        {getFollowupLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            minHeight="150px"
          >
            <CircularProgress />
          </Box>
        ) : !updates?.length ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="150px"
          >
            <Typography variant="body2" color="textSecondary">
              No Updates
            </Typography>
          </Box>
        ) : (
          updates?.map((item, idx) => {
            const isEditing = editData?.id === item?.id;

            return (
              <Paper
                elevation={1}
                key={idx}
                style={{
                  padding: "12px 16px",
                  marginBottom: "12px",
                  borderRadius: "10px",
                  background: "#fff",
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  {!isEditing && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditMessage(item)}
                    >
                      <Edit />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedUpdate(item);
                      setUpdateEvent(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>

                {isEditing ? (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      value={editData?.memo || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const updated = { ...editData };
                        updated.memo = value;
                        setEditData(updated);
                      }}
                      variant="outlined"
                      size="small"
                      style={{
                        marginBottom: 8,
                        borderRadius: 10,
                        backgroundColor: "#f9f9f9",
                      }}
                      InputProps={{
                        style: {
                          borderRadius: 6,
                        },
                      }}
                    />
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      gap={1}
                      mt={1}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => setEditData(null)}
                        style={{
                          borderRadius: 20,
                          textTransform: "none",
                          padding: "4px 16px",
                          marginRight: 8,
                        }}
                        disabled={createFollowupLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleUpdate(editData)}
                        style={{
                          borderRadius: 20,
                          textTransform: "none",
                          padding: "4px 16px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          background: "#2555ef",
                        }}
                        disabled={createFollowupLoading}
                      >
                        {createFollowupLoading ? (
                          <CircularProgress
                            size={11}
                            style={{ color: "#fff" }}
                          />
                        ) : (
                          "Update"
                        )}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    style={{ fontSize: 14, marginBottom: 8 }}
                  >
                    {item?.memo}
                  </Typography>
                )}

                <Box display="flex" alignItems="center" color="#888">
                  <Avatar
                    style={{
                      width: 28,
                      height: 28,
                      fontSize: 14,
                      marginRight: 8,
                      backgroundColor: "#ccc",
                    }}
                  >
                    {item?.changed_by?.[0]?.toUpperCase() || "U"}
                  </Avatar>
                  <Typography variant="caption">
                    Updated at{" "}
                    {formatDate({
                      date: item?.date,
                      formatType: "defaultWithTimeUTC",
                    })}{" "}
                    by {item?.changed_by || "Unknown"}
                  </Typography>
                </Box>
              </Paper>
            );
          })
        )}
      </>

      <MemberEventDrawerUpdates
        newUpdateField={newUpdateField}
        setUpdateValue={setUpdateValue}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        createFollowupLoading={createFollowupLoading}
        handleCloseNewUpdate={handleCloseNewUpdate}
        handleSubmitNewUpdate={handleSubmitNewUpdate}
        openNewTaskDrawer={openNewTaskDrawer}
        handleAddNewUpdate={handleAddNewUpdate}
      />
      {showEditDateModal && (
        <EditEventDateModal
          open={showEditDateModal}
          initialDate={new Date(eventDate)}
          onClose={() => setShowEditDateModal(false)}
          eventId={eventId}
          setCallFeedsAgain={setCallFeedsAgain}
          refetchEventCallback={refetchEventCallback}
        />
      )}

      {descriptionModal && (
        <EditEventDescriptionModal
          open={descriptionModal}
          initialDescription={description}
          onClose={() => setDescriptionModal(false)}
          eventId={eventId}
          refetchEventCallback={refetchEventCallback}
        />
      )}

      {updateEvent && (
        <AlertDialog
          open={updateEvent}
          onDecline={() => setUpdateEvent(false)}
          onAccept={() => {
            handleRemoveUpdate(selectedUpdate);
          }}
          title="Are you sure?"
          description={"Do you want to Delete this update?"}
          disabled={updateModalLoading}
        />
      )}
    </div>
  );
};

export default MemberEventDrawerContent;
